function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import { useRef, useState, useEffect } from 'react';
import { getIsStringDragAndDropFormat } from "../utilities/drag-and-drop.js";

/**
 * Drop hook for making an HTML element a dropzone.
 *
 * @example
 * const ref = useRef<HTMLDivElement | null>(null);
 * const { dropped, entered, over } = useDrop({ format: 'custom', onDropped: (data, e, format) => {}, ref });
 * <div ref={ref}>Drop</div>
 *
 * @param options UseDropOptions<T>
 * @returns UseDropReturn
 */
var useDrop = function useDrop(options) {
  var _options$delay = options.delay,
    delay = _options$delay === void 0 ? 1000 : _options$delay,
    _options$disabled = options.disabled,
    disabled = _options$disabled === void 0 ? false : _options$disabled,
    format = options.format,
    onDropped = options.onDropped,
    ref = options.ref;
  var htmlElement = ref === null || ref === void 0 ? void 0 : ref.current;
  var dragEnterTarget = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    dropped = _useState2[0],
    setDropped = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    entered = _useState4[0],
    setEntered = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    over = _useState6[0],
    setOver = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    mounted = _useState8[0],
    setMounted = _useState8[1];
  useEffect(function () {
    setMounted(true);
  }, []);
  useEffect(function () {
    if (disabled || !mounted || !htmlElement || !htmlElement.addEventListener) {
      return;
    }
    var typesMatchesFormat = function typesMatchesFormat(types) {
      if (Array.isArray(format)) {
        var formats = new Set(format.map(function (f) {
          return f.toLowerCase();
        }));
        return types.some(function (type) {
          return formats.has(type);
        });
      }
      return types.includes(format.toLowerCase());
    };
    var resetEnterOver = function resetEnterOver() {
      setEntered(false);
      setOver(false);
      dragEnterTarget.current = null;
    };
    var onDragEnter = function onDragEnter(e) {
      var _e$dataTransfer;
      e.preventDefault();
      var matches = typesMatchesFormat(((_e$dataTransfer = e.dataTransfer) === null || _e$dataTransfer === void 0 ? void 0 : _e$dataTransfer.types) || []);
      if (matches) {
        var _htmlElement$ondragen;
        dragEnterTarget.current = e.target;
        setEntered(true);
        (_htmlElement$ondragen = htmlElement.ondragenter) === null || _htmlElement$ondragen === void 0 || _htmlElement$ondragen.call(htmlElement, e);
      }
    };
    var onDragLeave = function onDragLeave(e) {
      var _e$dataTransfer2;
      var matches = typesMatchesFormat(((_e$dataTransfer2 = e.dataTransfer) === null || _e$dataTransfer2 === void 0 ? void 0 : _e$dataTransfer2.types) || []);
      if (matches && e.target === dragEnterTarget.current) {
        var _htmlElement$ondragle;
        e.preventDefault();
        (_htmlElement$ondragle = htmlElement.ondragleave) === null || _htmlElement$ondragle === void 0 || _htmlElement$ondragle.call(htmlElement, e);
        resetEnterOver();
      }
    };
    var onDragOver = function onDragOver(e) {
      var _e$dataTransfer3;
      e.preventDefault();
      var matches = typesMatchesFormat(((_e$dataTransfer3 = e.dataTransfer) === null || _e$dataTransfer3 === void 0 ? void 0 : _e$dataTransfer3.types) || []);
      if (matches) {
        var _htmlElement$ondragov;
        setOver(true);
        (_htmlElement$ondragov = htmlElement.ondragover) === null || _htmlElement$ondragov === void 0 || _htmlElement$ondragov.call(htmlElement, e);
      }
    };
    var resetDropped = function resetDropped() {
      setTimeout(function () {
        setDropped(false);
      }, delay);
    };
    var getData = function getData(e, format) {
      var _e$dataTransfer4;
      var data = (_e$dataTransfer4 = e.dataTransfer) === null || _e$dataTransfer4 === void 0 ? void 0 : _e$dataTransfer4.getData(format);
      if (data !== undefined && data !== '') {
        try {
          var isString = getIsStringDragAndDropFormat(format);
          var parsed = isString ? data : JSON.parse(data);
          onDropped(parsed, e, format);
        } catch (error) {
          console.error(error);
          // returning the unparsed string data is better than nothing
          onDropped(data, e, format);
        }
      }
    };
    var onData = function onData(e) {
      Array.isArray(format) ? format.map(function (format) {
        return getData(e, format);
      }) : getData(e, format);
      resetEnterOver();
      resetDropped();
    };
    var onDrop = function onDrop(e) {
      var _e$dataTransfer5;
      e.preventDefault();
      var matches = typesMatchesFormat(((_e$dataTransfer5 = e.dataTransfer) === null || _e$dataTransfer5 === void 0 ? void 0 : _e$dataTransfer5.types) || []);
      if (matches) {
        var _htmlElement$ondrop;
        setDropped(true);
        onData(e);
        (_htmlElement$ondrop = htmlElement.ondrop) === null || _htmlElement$ondrop === void 0 || _htmlElement$ondrop.call(htmlElement, e);
      }
    };
    htmlElement.addEventListener('dragenter', onDragEnter);
    htmlElement.addEventListener('dragover', onDragOver);
    htmlElement.addEventListener('dragleave', onDragLeave);
    htmlElement.addEventListener('drop', onDrop);
    return function () {
      htmlElement.removeEventListener('dragenter', onDragEnter);
      htmlElement.removeEventListener('dragover', onDragOver);
      htmlElement.removeEventListener('dragleave', onDragLeave);
      htmlElement.removeEventListener('drop', onDrop);
    };
  }, [delay, disabled, format, htmlElement, mounted, onDropped]);
  return {
    dropped: dropped,
    entered: entered,
    over: over
  };
};
export { useDrop };